import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { TextMultipleLangItem } from '@adornis/buchungsheld-db/entities/TextMultipleLangItem.js';
import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import { Language } from '@adornis/translation-core/languages.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { of } from 'rxjs';
import * as m from '../../../../localization/out/messages.js';
import { getJobPositionByID } from '../../../api/job-position.js';
import { subscribeJobApplicationsByUser } from '../../../api/job-application.js';
import { subscribeMatchingWeekSettings } from '../../../api/settings.js';
import { UserToolbar } from '../../components/user-toolbar.js';
import { translateEnum } from '../../translate-enum.js';
import { registerRouter } from './register-routes.js';

export const RegisterJobDetail = stateful((props: { jobPositionID: string }) => {
  const settingsQuery = useQuery(() => subscribeMatchingWeekSettings()({ applicationPhaseClosed: 1 }), []);
  const applicationPhaseClosed = settingsQuery.value?.applicationPhaseClosed !== false;
  const settingsLoaded = !settingsQuery.loading && settingsQuery.value !== undefined;
  const job = useQuery(
    async () =>
      getJobPositionByID(props.jobPositionID)({
        _id: 1,
        title: 1,
        employmentType: 1,
        company: {
          name: 1,
          logoID: 1,
          services: 1,
          website: 1,
          socialNetworks: { socialNetwork: 1, link: 1 },
          cultureAndValues: selectionSet(() => TextMultipleLangItem, 1),
          benefits: selectionSet(() => TextMultipleLangItem, 1),
        },
        description: selectionSet(() => TextMultipleLangItem, 1),
        location: 1,
        duration: 1,
        availableSlots: 1,
        startDate: 1,
        expectedAcademicTitle: 1,
        preferredStudyField: 1,
        languageRequirementsDE: 1,
        languageRequirementsEN: 1,
        languageRequirementsOther: 1,
        mandatoryTechSkills: 1,
        desiredTechSkills: 1,
        optionalTechSkills: 1,
        softSkills: 1,
        practicalExperience: selectionSet(() => TextMultipleLangItem, 1),
        category: { name: 1 },
        additionalRequirements: selectionSet(() => TextMultipleLangItem, 1),
      }),
    [props.jobPositionID],
  );

  const language = useObservable(() => globalLanguageSubject, []);

  const userObs = useObservable(() => currentUser<User>(), []);
  const applications = useQuery(() => {
    if (!userObs?._id) return of([]);
    return subscribeJobApplicationsByUser(userObs._id)({ jobPositionId: 1 });
  }, [userObs?._id]);
  const alreadyApplied = (applications.value ?? []).some(a => a.jobPositionId === props.jobPositionID);

  if (job.loading)
    return html`<div class="flex min-h-dvh flex-col">
      <div class="p-8 text-center"><span class="loading loading-lg"></span></div>
    </div>`;
  if (!job.value) return nothing;

  const jp = job.value;
  const companyInitials = (jp.company?.name ?? 'U')
    .split(/\s+/)
    .filter(Boolean)
    .map(p => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const mapToMdiLight = (ic?: string): string | undefined => {
    switch (ic) {
      case 'school':
        return 'icon-[material-symbols--school]';
      case 'work':
        return 'icon-[material-symbols--work]';
      case 'translate':
        return 'icon-[material-symbols--translate]';
      case 'schedule':
        return 'icon-[material-symbols--event-upcoming]';
      case 'group':
        return 'icon-[material-symbols--person-group]';
      case 'event':
        return 'icon-[material-symbols--calendar-today]';
      case 'category':
        return 'icon-[material-symbols--sell]';
      default:
        return undefined;
    }
  };

  const tag = (
    label: string,
    variant: 'primary' | 'secondary' | 'accent' | 'info' | 'success' | 'warning' | 'error' | 'ghost' = 'ghost',
    icon?: string,
  ) => {
    const mdi = mapToMdiLight(icon);
    return html`
      <span class=${`badge badge-${variant} badge-outline gap-1 items-center`}>
        ${mdi ? html`<span class="${mdi} text-sm"></span>` : nothing}${label}
      </span>
    `;
  };

  const translateEnumInternal = (et?: string) => {
    return translateEnum('job_form_enum', et);
  };

  const langLabel = (code: string) => (code === 'DE' ? m.german() : code === 'EN' ? m.english() : code);

  const formatDate = (d?: Date | string) => {
    if (!d) return '';
    try {
      const date = typeof d === 'string' ? new Date(d) : d;
      return format(date, 'dd.MM.yyyy');
    } catch {
      return String(d);
    }
  };

  const requirements = html`
    <div class="rounded-box">
      <h4 class="text-lg font-semibold mb-2">${m.requirements()}</h4>
      <ul class="list-disc ms-5 space-y-1">
        ${
          jp.preferredStudyField
            ? html`<li>
                ${m.study_field()}: ${jp.preferredStudyField.map(psf => translateEnumInternal(psf)).join(', ')}
              </li>`
            : nothing
        }
        ${
          jp.expectedAcademicTitle && Array.isArray(jp.expectedAcademicTitle) && jp.expectedAcademicTitle.length
            ? html`
                <li>
                  ${m.degree()}: ${jp.expectedAcademicTitle.map(title => translateEnumInternal(title)).join(', ')}
                </li>
              `
            : nothing
        }
        ${jp.startDate ? html`<li>${m.start_date()}: ${formatDate(jp.startDate)}</li>` : nothing}
        ${
          jp.languageRequirementsDE || jp.languageRequirementsEN
            ? html`<li>
                ${m.language_skills()}: ${jp.languageRequirementsDE ? `${m.german()} ${jp.languageRequirementsDE}` : ''}
                ${jp.languageRequirementsEN ? `; ${m.english()} ${jp.languageRequirementsEN}` : ''}
                ${jp.languageRequirementsOther ? `; ${jp.languageRequirementsOther}` : ''}
              </li>`
            : nothing
        }
        ${jp.mandatoryTechSkills ? html`<li>${m.mandatory_it_skills()}: ${jp.mandatoryTechSkills}</li>` : nothing}
        ${jp.desiredTechSkills ? html`<li>${m.desired_it_skills()}: ${jp.desiredTechSkills}</li>` : nothing}
        ${jp.optionalTechSkills ? html`<li>${m.optional_it_skills()}: ${jp.optionalTechSkills}</li>` : nothing}
        ${
          jp.practicalExperience && jp.practicalExperience.length
            ? html`
                <li>
                  ${m.practical_experience()}:
                  ${unsafeHTML(
                    TextMultipleLangItem.findByLanguage(
                      jp.practicalExperience as TextMultipleLangItem[],
                      (language as any) ?? Language.GERMAN,
                    )?.text ?? '',
                  )}
                </li>
              `
            : nothing
        }
        ${
          jp.softSkills && jp.softSkills.length
            ? html`<li>${m.soft_skills()}: ${jp.softSkills.map(s => translateEnumInternal(s)).join(', ')}</li>`
            : nothing
        }
      </ul>

      ${unsafeHTML(
        TextMultipleLangItem.findByLanguage(
          jp.additionalRequirements as TextMultipleLangItem[],
          language ?? Language.GERMAN,
        )?.text ?? '',
      )}
    </div>
  `;

  return html`
    <div class="flex flex-col min-h-dvh h-full overflow-y-auto">
      <div class="flex-1 overflow-y-auto">
        <div class="p-4 sm:p-8">
          <div class="max-w-4xl mx-auto">
            <div class="mb-4 flex flex-row items-center justify-between gap-2 sticky top-0 z-10 bg-base-100">
              <button class="btn btn-ghost" @click=${() => registerRouter.goBack('history')}>
                <span class="icon-[material-symbols--arrow-back]"></span>
                ${m.back()}
              </button>
              <div class="flex items-center gap-2">
                ${
                  alreadyApplied
                    ? html`
                        <div class="badge badge-neutral" title="${m.applied_badge_title()}">${m.applied_badge()}</div>
                      `
                    : applicationPhaseClosed
                      ? html`<button class="btn btn-primary btn-disabled shadow-lg" disabled>${m.apply_now()}</button>`
                      : html`
                          <a
                            class="btn btn-primary shadow-lg"
                            href=${registerRouter.link('register', { searchParams: { jobs: jp._id } })}
                          >
                            ${m.apply_now()}
                          </a>
                        `
                }
                ${UserToolbar()}
              </div>
            </div>
            ${
              settingsLoaded && applicationPhaseClosed
                ? html`
                    <div class="alert alert-warning shadow-lg mb-6">
                      <span class="icon-[material-symbols--warning] w-6 h-6"></span>
                      <div>
                        <h3 class="font-bold">${m.registration_closed_title()}</h3>
                        <div class="text-sm" .innerHTML=${m.registration_closed_message()}></div>
                      </div>
                    </div>
                  `
                : nothing
            }
            <section class="card bg-base-100 shadow-md">
              <div class="card-body">
                <div class="flex items-start gap-4">
                  ${
                    jp.company?.logoID
                      ? html`
                          <div class="avatar">
                            <div class="w-16 rounded">
                              <img
                                src="${AdornisFile.getServeLink(jp.company.logoID)}"
                                alt="${jp.company?.name ?? 'Logo'}"
                              />
                            </div>
                          </div>
                        `
                      : html`
                          <div class="avatar placeholder">
                            <div
                              class="w-16 h-16 rounded bg-neutral text-neutral-content flex items-center justify-center"
                            >
                              <span class="text-lg font-semibold">${companyInitials}</span>
                            </div>
                          </div>
                        `
                  }
                  <div class="flex-1">
                    <h2 class="text-2xl font-bold">${jp.company?.name ?? m.company()}</h2>
                    <p class="opacity-70">${jp.location ?? ''}</p>
                  </div>
                </div>
                <div class="mt-3 flex gap-2 flex-wrap">
                  ${
                    jp.preferredStudyField
                      ? jp.preferredStudyField.map(sf => tag(translateEnumInternal(sf), 'secondary', 'school'))
                      : nothing
                  }
                  ${jp.employmentType ? tag(translateEnumInternal(jp.employmentType), 'info', 'work') : nothing}
                  ${
                    jp.languageRequirementsDE
                      ? tag(`${langLabel('DE')} ${jp.languageRequirementsDE}`, 'success', 'translate')
                      : nothing
                  }
                  ${
                    jp.languageRequirementsEN
                      ? tag(`${langLabel('EN')} ${jp.languageRequirementsEN}`, 'success', 'translate')
                      : nothing
                  }
                  ${jp.duration ? tag(m.duration() + ' ' + jp.duration, 'primary', 'schedule') : nothing}
                  ${
                    jp.availableSlots
                      ? tag(
                          `${jp.availableSlots} ${jp.availableSlots > 1 ? m.slot_many() : m.slot_one()}`,
                          'accent',
                          'group',
                        )
                      : nothing
                  }
                  ${jp.startDate ? tag(`${formatDate(jp.startDate)}`, 'warning', 'event') : nothing}
                  ${jp.category?.name ? tag(jp.category.name, 'secondary', 'category') : nothing}
                </div>
              </div>
            </section>

            <h3 class="mt-12 text-xl ml-6 font-bold">${jp.title ?? m.job_title_fallback()}</h3>

            <section class="mt-6 card bg-base-100 shadow-md">
              <div class="card-body gap-8">
                <div class="mb-3">
                  <h4 class="text-lg font-semibold mb-2">${m.job_description()}</h4>
                  <div class="prose max-w-none">
                    ${unsafeHTML(
                      TextMultipleLangItem.findByLanguage(
                        jp.description as TextMultipleLangItem[],
                        language ?? Language.GERMAN,
                      )?.text ?? '',
                    )}
                  </div>
                </div>

                <div>${requirements}</div>

                ${
                  jp.company?.services ||
                  (jp.company?.cultureAndValues as any[])?.length ||
                  (jp.company?.benefits as any[])?.length ||
                  jp.company?.website ||
                  (jp.company?.socialNetworks as any[])?.length
                    ? html`
                        <div class="mb-3 flex flex-col gap-4">
                          <h4 class="text-lg font-semibold mb-2">${m.company()}</h4>
                          <div class="gap-4">
                            ${
                              jp.company?.services
                                ? html`<div>
                                    <div class="font-semibold mb-1">${m.services()}</div>
                                    <p class="whitespace-pre-wrap">${jp.company.services}</p>
                                  </div>`
                                : nothing
                            }
                          </div>

                          <div class="gap-4">
                            ${
                              (jp.company?.cultureAndValues as any[])?.length
                                ? html`<div>
                                    <div class="font-semibold mb-1">${m.culture_values()}</div>
                                    <p>
                                      ${unsafeHTML(
                                        TextMultipleLangItem.findByLanguage(
                                          jp.company?.cultureAndValues as TextMultipleLangItem[],
                                          language ?? Language.GERMAN,
                                        )?.text,
                                      )}
                                    </p>
                                  </div>`
                                : nothing
                            }
                          </div>

                          <div class="gap-4">
                            ${
                              (jp.company?.benefits as any[])?.length
                                ? html`<div>
                                    <div class="font-semibold mb-1">${m.benefits()}</div>
                                    <p>
                                      ${unsafeHTML(
                                        TextMultipleLangItem.findByLanguage(
                                          jp.company?.benefits as TextMultipleLangItem[],
                                          language ?? Language.GERMAN,
                                        )?.text,
                                      )}
                                    </p>
                                  </div>`
                                : nothing
                            }
                          </div>

                          ${
                            jp.company?.website
                              ? html`
                                  <div class="gap-4">
                                    <div class="font-semibold mb-1">${m.website()}</div>
                                    <a
                                      class="link link-primary"
                                      href="${jp.company.website}"
                                      target="_blank"
                                      rel="noopener"
                                      >${jp.company.website}</a
                                    >
                                  </div>
                                `
                              : nothing
                          }

                          <div class="gap-4">
                            ${
                              (jp.company?.socialNetworks as any[])?.length
                                ? html`<div>
                                    <div class="font-semibold mb-1">${m.social_media()}</div>
                                    <ul class="list-disc ms-5 space-y-1">
                                      ${(jp.company?.socialNetworks as any[]).map(
                                        sn =>
                                          html`<li>
                                            <a
                                              class="link link-primary"
                                              href="${sn?.link ?? '#'}"
                                              target="_blank"
                                              rel="noopener"
                                            >
                                              ${sn?.socialNetwork ?? ''}
                                            </a>
                                          </li>`,
                                      )}
                                    </ul>
                                  </div>`
                                : nothing
                            }
                          </div>
                        </div>
                      `
                    : nothing
                }
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  `;
});
